import { CertificateType, CertificateStatus } from '../_constants';

function findTypeId(certificateTypes, certType) {
  return (certificateTypes || []).find((type) => type.name == certType)?.id;
}

function statusBasedCertTypeList(certificateTypes, status) {
  const basic40Id = findTypeId(certificateTypes, CertificateType.BASIC40);
  const s130Id = findTypeId(certificateTypes, CertificateType.S130);
  const s190Id = findTypeId(certificateTypes, CertificateType.S190);
  const l180Id = findTypeId(certificateTypes, CertificateType.L180);
  const femaIS100Id = findTypeId(certificateTypes, CertificateType.FEMA_IS100);
  const femaIS700Id = findTypeId(certificateTypes, CertificateType.FEMA_IS700);
  const rt130Id = findTypeId(certificateTypes, CertificateType.RT130);
  const resumeId = findTypeId(certificateTypes, CertificateType.RESUME);
  const fireHistoryId = findTypeId(
    certificateTypes,
    CertificateType.FIRE_HISTORY,
  );
  const firstAidCprId = findTypeId(
    certificateTypes,
    CertificateType.FIRSTAID_CPR,
  );
  const wctId = findTypeId(certificateTypes, CertificateType.WCT);

  switch (status) {
    case CertificateStatus.NO_TRAINING:
      // Required: Resume. Optional: FEMA IS-100, FEMA IS-700, First-aid CPR
      return [resumeId, femaIS100Id, femaIS700Id, firstAidCprId];
    case CertificateStatus.COMPLETED_BASIC_TRAINING:
      // Required: Resume, FEMA IS-100, FEMA IS-700, First-aid CPR, Basic 40 OR (S-130 + S-190 + L-180). Optional: WCT
      return [
        resumeId,
        basic40Id,
        s130Id,
        s190Id,
        l180Id,
        femaIS100Id,
        femaIS700Id,
        firstAidCprId,
        wctId,
      ];
    case CertificateStatus.NEED_RT130:
      // Required: Resume, FEMA IS-100, FEMA IS-700, First-aid CPR, Basic 40 OR (S-130 + S-190 + L-180). Optional: WCT, fire history
      return [
        resumeId,
        basic40Id,
        s130Id,
        s190Id,
        l180Id,
        femaIS100Id,
        femaIS700Id,
        firstAidCprId,
        wctId,
        fireHistoryId,
      ];
    case CertificateStatus.COMPLETED_RT130:
      // Required: Resume, FEMA IS-100, FEMA IS-700, First-aid CPR, Basic 40 OR (S-130 + S-190 + L-180), RT-130, WCT. Optional: fire history
      return [
        resumeId,
        basic40Id,
        s130Id,
        s190Id,
        l180Id,
        femaIS100Id,
        femaIS700Id,
        rt130Id,
        firstAidCprId,
        wctId,
        fireHistoryId,
      ];
    default:
      return [];
  }
}

// Checks whether the uploaded certificates (userCertIds) satisfy the
// documents required for the given certification status name (certName).
function validateCertificationDocuments(certificateTypes, certName, userCertIds) {
  const basic40Id = findTypeId(certificateTypes, CertificateType.BASIC40);
  const s130Id = findTypeId(certificateTypes, CertificateType.S130);
  const s190Id = findTypeId(certificateTypes, CertificateType.S190);
  const l180Id = findTypeId(certificateTypes, CertificateType.L180);
  const femaIS100Id = findTypeId(certificateTypes, CertificateType.FEMA_IS100);
  const femaIS700Id = findTypeId(certificateTypes, CertificateType.FEMA_IS700);
  const rt130Id = findTypeId(certificateTypes, CertificateType.RT130);
  const resumeId = findTypeId(certificateTypes, CertificateType.RESUME);
  const firstAidCprId = findTypeId(
    certificateTypes,
    CertificateType.FIRSTAID_CPR,
  );
  const wctId = findTypeId(certificateTypes, CertificateType.WCT);

  const ids = userCertIds || [];
  let valid = false;
  let errorMsg = '';

  switch (certName) {
    case CertificateStatus.NO_TRAINING:
      valid = ids.includes(resumeId);
      errorMsg = 'You must upload your resume.';
      break;
    case CertificateStatus.NEED_RT130:
      valid =
        ids.includes(resumeId) &&
        (ids.includes(basic40Id) ||
          (ids.includes(s130Id) && ids.includes(s190Id) && ids.includes(l180Id))) &&
        ids.includes(femaIS100Id) &&
        ids.includes(femaIS700Id) &&
        ids.includes(firstAidCprId);
      errorMsg =
        'You must upload all of the following required certificates: <ol><li>Resume</li><li>FEMA IS-100</li><li>FEMA IS-700</li><li>First-aid CPR</li><li>Basic 40 or a combination of S-130, S-190 and L-180</li></ol>';
      break;
    case CertificateStatus.COMPLETED_BASIC_TRAINING:
      valid =
        ids.includes(resumeId) &&
        (ids.includes(basic40Id) ||
          (ids.includes(s130Id) && ids.includes(s190Id) && ids.includes(l180Id))) &&
        ids.includes(femaIS100Id) &&
        ids.includes(femaIS700Id) &&
        ids.includes(firstAidCprId);
      errorMsg =
        'You must upload all of the following required certificates: <ol><li>Resume</li><li>FEMA IS-100</li><li>FEMA IS-700</li><li>First-aid CPR</li><li>Basic 40 or a combination of S-130, S-190 and L-180</li></ol>';
      break;
    case CertificateStatus.COMPLETED_RT130:
      valid =
        ids.includes(resumeId) &&
        ids.includes(rt130Id) &&
        (ids.includes(basic40Id) ||
          (ids.includes(s130Id) && ids.includes(s190Id) && ids.includes(l180Id))) &&
        ids.includes(femaIS100Id) &&
        ids.includes(femaIS700Id) &&
        ids.includes(firstAidCprId) &&
        ids.includes(wctId);
      errorMsg =
        'You must upload all of the following required certificates: <ol><li>Resume</li><li>RT-130</li><li>FEMA IS-100</li><li>FEMA IS-700</li><li>First-aid CPR</li><li>WCT</li><li>Basic 40 or a combination of S-130, S-190 and L-180</li></ol>';
      break;
    default:
      valid = false;
      errorMsg = 'Unexpected Error Occurred.';
  }

  return { valid, errorMsg };
}

export { findTypeId, statusBasedCertTypeList, validateCertificationDocuments };
